import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { ScoringInput, ScoringResult } from "@/lib/scoring-engine";
import { buildDocumentIntelligenceContext, type NormalizedDocumentIntelligence, type ValidationStatus } from "@/lib/document-intelligence";

type DealDocumentRow = Tables<"deal_documents">;

export interface DealPackageSections {
  deal_overview: boolean;
  key_metrics: boolean;
  risk_summary: boolean;
  top_risks: boolean;
  underwriting_adjustments: boolean;
  supporting_evidence: boolean;
  opportunity_summary: boolean;
  sensitivity_snapshot: boolean;
  next_steps: boolean;
}

export interface ExportInsightItem {
  title: string;
  detail?: string;
  source?: string;
}

export interface ExportDocumentSnapshot {
  document_id: string;
  title: string;
  lane: string;
  subtype: string | null;
  confidence_label: string;
  confidence_score: number;
  validation_status: ValidationStatus | null;
  top_risk?: ExportInsightItem;
  top_adjustment?: ExportInsightItem;
  evidence_count: number;
}

export interface DealExportIntelligence {
  documents: ExportDocumentSnapshot[];
  top_risks: ExportInsightItem[];
  underwriting_adjustments: ExportInsightItem[];
  evidence_highlights: ExportInsightItem[];
  investment_readout: {
    bottom_line: string;
    top_risks: ExportInsightItem[];
    validated_strengths: ExportInsightItem[];
    confidence_label: string;
    confidence_score: number;
    recommended_next_step: string;
  };
  confidence_summary: {
    average_score: number;
    label: string;
    low_confidence_documents: number;
    document_count: number;
  };
  validation_breakdown: Record<"strong" | "partial" | "weak" | "failed" | "unscored", number>;
}

function prettifyLabel(value: string | null | undefined) {
  if (!value) return "Unknown";
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function asInsightItem(value: unknown): ExportInsightItem | null {
  if (!value) return null;
  if (typeof value === "string") return { title: value };
  if (typeof value === "number" || typeof value === "boolean") return { title: String(value) };
  if (typeof value === "object" && !Array.isArray(value)) {
    const source = value as Record<string, unknown>;
    const title = [source.title, source.label, source.risk, source.adjustment, source.summary, source.issue, source.name]
      .find((item) => typeof item === "string" && item.trim().length > 0);
    const detail = [source.detail, source.reason, source.rationale, source.description, source.impact, source.note, source.value]
      .find((item) => typeof item === "string" && item.trim().length > 0);
    const docRef = [source.source, source.document, source.document_type, source.lane]
      .find((item) => typeof item === "string" && item.trim().length > 0);

    if (typeof title === "string") {
      return {
        title,
        detail: typeof detail === "string" ? detail : undefined,
        source: typeof docRef === "string" ? docRef : undefined,
      };
    }

    const entries = Object.entries(source).filter(([, item]) => item != null && item !== "");
    if (entries.length === 0) return null;
    const [firstKey, firstValue] = entries[0];
    return {
      title: `${prettifyLabel(firstKey)}: ${String(firstValue)}`,
      detail: entries.length > 1 ? entries.slice(1, 3).map(([key, item]) => `${prettifyLabel(key)}: ${String(item)}`).join(" • ") : undefined,
    };
  }
  return null;
}

function confidenceLabel(score: number) {
  // Unified platform tier — see src/lib/confidence.ts.
  if (score >= 80) return "High";
  if (score >= 50) return "Moderate";
  return "Low";
}

function dedupeInsights(items: ExportInsightItem[], limit: number) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = `${item.title}::${item.detail || ""}`.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, limit);
}

function attachSource(item: ExportInsightItem | null, document: NormalizedDocumentIntelligence) {
  if (!item) return null;
  return {
    ...item,
    source: item.source || prettifyLabel(document.document_subtype || document.document_lane || document.doc_type),
  };
}

function inferNextStep(topRisk: ExportInsightItem | null, topAdjustment: ExportInsightItem | null, lowConfidenceDocuments: number) {
  const combined = `${topRisk?.title || ""} ${topRisk?.detail || ""} ${topAdjustment?.title || ""} ${topAdjustment?.detail || ""}`.toLowerCase();

  if (lowConfidenceDocuments > 0 || /missing|incomplete|weak|unsupported|unclear|thin evidence/.test(combined)) {
    return "Review missing evidence and low-trust outputs before relying on this document set in the investment case.";
  }
  if (/rent|lease|loss to lease|comp|concession/.test(combined)) {
    return "Verify rent assumptions against in-place performance, concessions, and market comps before finalizing upside.";
  }
  if (/occup|vacan|delinquen|collections/.test(combined)) {
    return "Validate occupancy and collections claims against unit-level evidence before underwriting to the current revenue base.";
  }
  if (/expense|tax|insurance|utility|repair|noi|opex/.test(combined)) {
    return "Confirm expense realism and normalize unusual operating items before relying on the projected NOI.";
  }
  if (/capex|roof|hvac|plumbing|electrical|inspection|deferred maintenance|scope|budget/.test(combined)) {
    return "Review CapEx scope, exclusions, and deferred maintenance exposure before locking renovation assumptions.";
  }
  if (/loan|lender|debt|interest|rate|amort/.test(combined)) {
    return "Confirm lender terms and debt assumptions before treating the financing plan as dependable.";
  }

  return topAdjustment?.title
    ? `Underwrite to ${topAdjustment.title.charAt(0).toLowerCase()}${topAdjustment.title.slice(1)} before sharing this deal externally.`
    : "Use the strongest corroborated documents to validate the highest-risk assumptions before moving forward.";
}

function buildBottomLine({
  documentCount,
  averageScore,
  lowConfidenceDocuments,
  strongCount,
  partialCount,
}: {
  documentCount: number;
  averageScore: number;
  lowConfidenceDocuments: number;
  strongCount: number;
  partialCount: number;
}) {
  if (documentCount === 0) {
    return "No document intelligence has been incorporated yet, so the current readout is not externally robust.";
  }
  if (averageScore >= 80 && lowConfidenceDocuments === 0 && strongCount > 0) {
    return "The document set broadly supports the underwriting story with high trust and limited unresolved evidence gaps.";
  }
  if (averageScore >= 55 && strongCount + partialCount > 0) {
    return "The document set supports the deal direction, but several assumptions still require targeted verification before conviction is high.";
  }
  return "The document set is still directional; key assumptions rely on weak or incomplete support and should be pressure-tested before relying on them.";
}

function buildValidatedStrengths(documents: NormalizedDocumentIntelligence[]) {
  const strengths = documents
    .filter((document) => (document.validation_status === "strong" || document.validation_status === "partial") && document.confidence.score >= 55)
    .flatMap((document) => {
      const source = prettifyLabel(document.document_subtype || document.document_lane || document.doc_type);
      const evidenceItems = document.evidence_log
        .map(asInsightItem)
        .filter((item): item is ExportInsightItem => Boolean(item))
        .slice(0, 2)
        .map((item) => ({
          ...item,
          source: item.source || source,
        }));

      if (evidenceItems.length > 0) return evidenceItems;

      if (document.executive_interpretation) {
        return [{
          title: document.executive_interpretation.split(/[.!?]/).find((sentence) => sentence.trim().length > 0)?.trim() || document.executive_interpretation,
          source,
        }];
      }

      return [{
        title: `${source} validated with ${document.confidence.level.toLowerCase()} confidence`,
        source,
      }];
    });

  return dedupeInsights(strengths, 2);
}

export function buildDealExportIntelligence(documents: NormalizedDocumentIntelligence[]): DealExportIntelligence {
  const validationBreakdown = {
    strong: 0,
    partial: 0,
    weak: 0,
    failed: 0,
    unscored: 0,
  } satisfies Record<"strong" | "partial" | "weak" | "failed" | "unscored", number>;

  const topRisks: ExportInsightItem[] = [];
  const adjustments: ExportInsightItem[] = [];
  const evidence: ExportInsightItem[] = [];

  const snapshots = documents.map((document) => {
    const title = prettifyLabel(document.document_subtype || document.document_lane || document.doc_type);
    const topRisk = attachSource(document.risk_flags.map(asInsightItem).find(Boolean) ?? null, document);
    const topAdjustment = attachSource(document.recommended_underwriting_adjustments.map(asInsightItem).find(Boolean) ?? null, document);

    const evidenceItems = document.evidence_log
      .map(asInsightItem)
      .filter((item): item is ExportInsightItem => Boolean(item))
      .map((item) => ({ ...item, source: item.source || title }));

    if (!document.validation_status) {
      validationBreakdown.unscored += 1;
    } else {
      validationBreakdown[document.validation_status] += 1;
    }

    if (topRisk) topRisks.push(topRisk);
    if (topAdjustment) adjustments.push(topAdjustment);
    evidence.push(...evidenceItems);

    return {
      document_id: document.document_id,
      title,
      lane: prettifyLabel(document.document_lane || document.doc_type),
      subtype: document.document_subtype ? prettifyLabel(document.document_subtype) : null,
      confidence_label: confidenceLabel(document.confidence.score),
      confidence_score: document.confidence.score,
      validation_status: document.validation_status,
      top_risk: topRisk || undefined,
      top_adjustment: topAdjustment || undefined,
      evidence_count: evidenceItems.length,
    } satisfies ExportDocumentSnapshot;
  });

  const scores = documents.map((document) => document.confidence.score).filter((score) => Number.isFinite(score));
  const averageScore = scores.length > 0 ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : 0;
  const dedupedTopRisks = dedupeInsights(topRisks, 5);
  const dedupedAdjustments = dedupeInsights(adjustments, 5);
  const validatedStrengths = buildValidatedStrengths(documents);
  const lowConfidenceDocuments = documents.filter((document) => document.confidence.score < 55).length;

  return {
    documents: snapshots,
    top_risks: dedupedTopRisks,
    underwriting_adjustments: dedupedAdjustments,
    evidence_highlights: dedupeInsights(evidence, 5),
    investment_readout: {
      bottom_line: buildBottomLine({
        documentCount: documents.length,
        averageScore,
        lowConfidenceDocuments,
        strongCount: validationBreakdown.strong,
        partialCount: validationBreakdown.partial,
      }),
      top_risks: dedupedTopRisks.slice(0, 3),
      validated_strengths: validatedStrengths,
      confidence_label: confidenceLabel(averageScore),
      confidence_score: averageScore,
      recommended_next_step: inferNextStep(dedupedTopRisks[0] || null, dedupedAdjustments[0] || null, lowConfidenceDocuments),
    },
    confidence_summary: {
      average_score: averageScore,
      label: confidenceLabel(averageScore),
      low_confidence_documents: lowConfidenceDocuments,
      document_count: documents.length,
    },
    validation_breakdown: validationBreakdown,
  };
}

export async function fetchDealExportIntelligence(dealId: string | null | undefined): Promise<DealExportIntelligence | null> {
  if (!dealId) return null;

  const { data, error } = await supabase
    .from("deal_documents")
    .select("id, doc_type, document_lane, document_subtype, risk_flags, recommended_underwriting_adjustments, confidence, executive_interpretation, brickshare_field_mapping, evidence_log, extracted_data")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  const normalized = buildDocumentIntelligenceContext((data || []) as DealDocumentRow[]).documents;
  return buildDealExportIntelligence(normalized);
}

export async function createDealSharePackage({
  dealId,
  context,
  scoringInput,
  result,
  visibleSections,
}: {
  dealId: string;
  context: Record<string, unknown>;
  scoringInput: ScoringInput;
  result: ScoringResult;
  visibleSections?: DealPackageSections;
}) {
  const exportIntelligence = await fetchDealExportIntelligence(dealId);
  const m = result.derived;
  const negotiationInsights: string[] = [];
  const pp = Number(context.purchase_price || scoringInput.purchase_price) || 0;
  const capex = Number(context.capex_reserves || scoringInput.capex_total || scoringInput.capex_reserves) || 0;
  const ltv = Number(scoringInput.ltv || context.ltv) || 0;
  const rate = Number(scoringInput.interest_rate || context.interest_rate) || 0;

  if (pp > 0) negotiationInsights.push(`Purchase price ≤ $${Math.round(pp * 0.95).toLocaleString("en-US")}`);
  if (capex > 0) negotiationInsights.push(`CapEx budget ≤ $${capex.toLocaleString("en-US")}`);
  if (m.dscr > 0) {
    const maxLTV = ltv > 0 && m.dscr >= 1.25 ? ltv : ltv > 0 ? Math.min(ltv, 70) : 70;
    negotiationInsights.push(`Maximum LTV to maintain DSCR target: ${maxLTV}%`);
  }
  if (rate > 0) negotiationInsights.push(`Underwritten at ${rate.toFixed(2)}% interest rate`);
  if (m.breakeven_occupancy > 0) {
    negotiationInsights.push(`Breakeven occupancy at ${m.breakeven_occupancy.toFixed(0)}% — verify lease-up timeline`);
  }

  const sharePayload = {
    context,
    metrics: m,
    result: JSON.parse(JSON.stringify(result)),
    score: result.overall_score,
    verdict: result.verdict,
    verdict_label: result.verdict_label,
    risk_level: result.risk_level,
    confidence: result.confidence,
    drivers: result.drivers,
    flags: result.flags,
    sensitivity: result.sensitivity,
    actions: result.actions,
    negotiation_insights: negotiationInsights.length > 0 ? negotiationInsights : null,
    visible_sections: visibleSections || null,
    document_intelligence: exportIntelligence,
    generated_at: new Date().toISOString(),
  };

  const units = context.unit_count || "deal";
  const market = (context.market_type || "").toString().toLowerCase();
  const shortId = dealId.slice(0, 8);
  const slug = `${units}-unit-${market || "market"}-${shortId}`;

  const { error } = await supabase
    .from("deals")
    .update({
      shared: true,
      share_slug: slug,
      share_payload: sharePayload as never,
    })
    .eq("id", dealId);

  if (error) throw error;

  return {
    slug,
    sharePayload,
    shareUrl: `${window.location.origin}/deal-summary/${slug}`,
  };
}